/* eslint-disable camelcase */

import { XO_SESSION_TOKEN } from '@constants/membership';
import { Member } from '@typings/membership';
import { determineReferredMarketCode } from '@utils';
import { formatSavedVendorData } from '@utils/formatSavedVendorData';
import { getPlatform } from '@utils/getPlatform';
import { Logger } from '@utils/logger/logger';
import savedVendors from '@utils/savedVendors';
import WeddingSDK from '@xo-union/sdk-weddings';
import { CookieStorage } from 'cookie-storage';
import type { InlineRfqSourceContent } from 'types/sourceContent';
import { cookieGetItem } from '../../../client/utils/cookie/consentManagementCookie';
import { VENDOR_REFERRED_MARKET_CODE } from '../../../constants/cookies';
import { getVisitId, getVisitorId } from '../../../utils/cookies';
import MembershipApi from '../api/membership';
import WeddingsApi from '../api/weddings';
import { API_REQUEST_ERRORS } from '../constants';
import LocalStorage from '../data/loader/local_storage';
import { localStorageCommonProps } from '../data/loader/utils/general';
import guestCountConfig from '../rfqConfig/guestCount';
import { formatApiRequestError } from './apiErrors.utils';

interface BuildInquiryDataProps {
	anonymousId: string;
	fields: Rfq.InlineFields;
	flexibleWeddingDate: boolean;
	vendor: Vendor.Decorated | null;
	isFromSearch?: boolean;
	isReferred?: boolean;
	marketCode?: string;
	member: Membership.Member | null;
	initiator: string;
	overrideTextBody?: string;
	dealId?: string;
}

const PW_REQUIREMENTS_LIST = [
	'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
	'abcdefghijklmnopqrstuvwxyz',
	'0123456789',
	'!@#$%^&*',
];

const pickRandomValue = (valuesToChooseFrom: string) => {
	const LIMIT = 4;
	return Array.from(crypto.getRandomValues(new Uint32Array(LIMIT)))
		.map((item) => valuesToChooseFrom[item % valuesToChooseFrom.length])
		.join('');
};

const shuffleString = (unshuffledString: string): string => {
	const shuffled = unshuffledString
		.split('')
		.sort(() => 0.5 - Math.random())
		.join('');

	if (shuffled !== unshuffledString) {
		return shuffled;
	}

	return shuffleString(shuffled);
};

const generateTemporaryPassword = () => {
	const unshuffledString = PW_REQUIREMENTS_LIST.map((requirement) =>
		pickRandomValue(requirement),
	).join('');
	return shuffleString(unshuffledString);
};

const signUpUser = async ({
	signUpDetails,
	membershipService,
	apiRequestErrors,
}: AutoAccountCreation.SignUpUser & {
	apiRequestErrors?: Rfq.ApiRequestErrors;
}) => {
	return membershipService?.membershipService
		.signUp({ ...signUpDetails })
		.catch((error) => {
			if (apiRequestErrors) {
				apiRequestErrors.push(
					formatApiRequestError(API_REQUEST_ERRORS.MEMBERSHIP_SIGNUP, error),
				);
			}
			Logger.error({
				log_message: 'Error signing up user',
				errorToLog: error,
				newRelic: true,
			});
			throw error;
		});
};

const getTokenForUser = async (
	membershipService: MembershipService.Service,
	apiRequestErrors?: Rfq.ApiRequestErrors,
) => {
	try {
		const sessionToken =
			await membershipService.membershipService.getSessionToken();
		return sessionToken;
	} catch (error) {
		if (apiRequestErrors) {
			apiRequestErrors.push(
				formatApiRequestError(API_REQUEST_ERRORS.MEMBERSHIP_TOKEN, error),
			);
		}
		throw error;
	}
};

const upsertWeddingForUser = async ({
	memberUuid,
	token,
	weddingDate,
	guestCount,
	apiRequestErrors,
}: AutoAccountCreation.UpsertWeddingForUser & {
	apiRequestErrors?: Rfq.ApiRequestErrors;
}) => {
	const weddingClient = new WeddingSDK({
		apiKey: process.env.WEDDINGS_API_KEY as string,
		membershipSessionToken: token,
	});
	return weddingClient
		.upsertWedding({
			wedding: {
				member_id: memberUuid,
				wedding_date: weddingDate,
				guest_range: guestCount,
			},
		})
		.catch((error) => {
			if (apiRequestErrors) {
				apiRequestErrors.push(
					formatApiRequestError(API_REQUEST_ERRORS.WEDDING_SDK_UPSERT, error),
				);
			}
			throw error;
		});
};

const upsertWeddingDateForUser = async (
	memberUuid: string,
	token: string,
	weddingDate: string,
) => {
	return await upsertWeddingForUser({
		memberUuid,
		token,
		weddingDate,
		guestCount: null,
	});
};

const logInUser = ({
	logInDetails,
	membershipService,
}: AutoAccountCreation.LogInUser) => {
	return membershipService.membershipService.logIn({ ...logInDetails });
};

export const PasswordSessionStorageKeyName = 'tkww-aac-temp';

const fetchTemporaryPassword = () =>
	window?.sessionStorage?.getItem(PasswordSessionStorageKeyName);

const changeUserPassword = ({
	changePasswordDetails,
	membershipService,
}: AutoAccountCreation.ChangeUserPassword) => {
	return membershipService.membershipService.changePassword({
		...changePasswordDetails,
	});
};

const determineFieldValues = (state: Redux.State, type?: string) => {
	if (type === 'inline') {
		const { emailAddress, firstName, lastName, weddingDate, guestCount } =
			state.rfq.inline.fields;
		return {
			emailAddress: emailAddress.value,
			firstName: firstName.value,
			lastName: lastName.value,
			weddingDate: weddingDate.value,
			guestCount: guestCount.value,
		};
	}
	const {
		emailAddress = '',
		firstName = '',
		lastName = '',
		guest_count = '',
		xo_date_picker,
	} = state.rfq.rfqFormData;
	const weddingDate = xo_date_picker?.weddingDate || '';
	return {
		emailAddress,
		firstName,
		lastName,
		guestCount: String(guest_count),
		weddingDate,
	};
};

const normalizeGuestCountValue = (guestCount?: string | number | null) => {
	if (
		guestCount === null ||
		guestCount === undefined ||
		(typeof guestCount === 'string' && guestCount.trim() === '')
	) {
		return '';
	}

	const value = String(guestCount).trim();
	const normalizedRange = value.replace(/\s*-\s*/g, '–');
	if (normalizedRange.includes('–') || normalizedRange === '300+') {
		return normalizedRange;
	}

	const numericGuestCount = Number(normalizedRange.replace('+', ''));
	if (Number.isNaN(numericGuestCount)) {
		return normalizedRange;
	}

	const selectedGuestCount = guestCountConfig.property.values.find(
		({ value: { minGuestCount, maxGuestCount } }) =>
			numericGuestCount >= minGuestCount &&
			(maxGuestCount === 0 || numericGuestCount <= maxGuestCount),
	);

	return selectedGuestCount?.display || normalizedRange;
};

const buildSenderInfo = (
	fields: Rfq.InlineFields,
	member: Membership.Member | null,
	flexibleWeddingDate: boolean,
) => {
	const {
		emailAddress,
		firstName,
		guestCount,
		lastName,
		phoneNumber,
		weddingDate,
	} = fields;
	const normalizedGuestCount = normalizeGuestCountValue(guestCount.value);
	const [minGuestCount, maxGuestCount] =
		normalizedGuestCount === '300+'
			? [301, 0]
			: normalizedGuestCount.split('–');
	const phoneAndContactInfo = phoneNumber.value
		? { phone: phoneNumber.value }
		: {};
	const senderInfo = {
		details: { flexibleWeddingDate },
		email: emailAddress.value,
		maxGuestCount: Number(maxGuestCount),
		minGuestCount: Number(minGuestCount),
		name: { first: firstName.value, last: lastName.value },
		weddingDate: weddingDate.value,
		...phoneAndContactInfo,
	};

	return member
		? { senderInfo: { ...senderInfo, id: member.id } }
		: { senderAnonymousInfo: senderInfo };
};

const buildInquiryData = (props: BuildInquiryDataProps) => {
	const {
		anonymousId,
		fields,
		flexibleWeddingDate,
		isFromSearch,
		isReferred,
		marketCode,
		member,
		initiator,
		vendor,
		overrideTextBody,
		dealId,
	} = props;
	const {
		emailAddress,
		firstName,
		guestCount,
		lastName,
		phoneNumber,
		textarea,
		weddingDate,
	} = fields;
	const sendInfo = buildSenderInfo(fields, member, flexibleWeddingDate);
	const visitId = getVisitId();
	const visitorId = getVisitorId();
	const referredMarketCode = determineReferredMarketCode({
		cookieMarketCode: cookieGetItem(VENDOR_REFERRED_MARKET_CODE),
		isFromSearch,
		isReferred,
		marketCode,
	});
	const visitIds = visitId && visitorId ? { visitId, visitorId } : {};

	return {
		anonymousId,
		body: overrideTextBody || textarea.value,
		email: emailAddress.value,
		first_name: firstName.value,
		isRfq: true,
		isVRT: false,
		last_name: lastName.value,
		leadFormType: 'direct',
		number_of_guests: normalizeGuestCountValue(guestCount.value),
		...sendInfo,
		source: {
			application: 'theknot',
			categoryCode: vendor?.categoryCode,
			feature: 'beta',
			marketCode: vendor?.marketCode,
			platform: getPlatform(),
		},
		sourceContent: initiator,
		sourcePage: vendor?.isPaid ? 'paid storefront' : 'unpaid storefront',
		storefrontId: vendor?.id,
		tags: ['INBOX'],
		user_email: emailAddress.value,
		vendorReferredMarketCode: referredMarketCode,
		wedding_date: weddingDate.value,
		...visitIds,
		...(dealId ? { dealId } : {}),
	};
};

const buildRfqDataFromMember = (member: Member | null) => {
	return {
		isDateFlexible: false,
		fields: {
			emailAddress: {
				value: member?.email || '',
			},
			firstName: {
				value: member?.first_name || '',
			},
			lastName: {
				value: member?.last_name || '',
			},
			guestCount: {
				value: normalizeGuestCountValue(member?.number_of_guests),
			},
			phoneNumber: {
				value: member?.phone_number || '',
			},
			weddingDate: {
				value: member?.wedding_date || '',
			},
		},
	};
};

const updateLocalStorage = (
	formData: Rfq.FormData,
	code: Category.CategoryCode,
) => {
	const current = LocalStorage.get() || {};
	const commonProps: Partial<Rfq.LocalStorageCommonProps> = {};
	const data = JSON.parse(JSON.stringify(formData));

	localStorageCommonProps().forEach((key) => {
		if (key in data) {
			commonProps[key as Rfq.CommonPropKeys] = data[key];
			delete data[key];
		}
	});

	const categoryData: Partial<Rfq.LocalStorageCategoryData> = {};
	if (Object.keys(data).length) {
		categoryData[code] = data;
	}

	const output = Object.assign(current, categoryData, commonProps);
	LocalStorage.set(output);
};

const updateWeddings = (
	formData: Rfq.FormData,
	category: Category.CategoryCode,
	member: Redux.Membership['member'],
	apiRequestErrors?: Rfq.ApiRequestErrors,
) => {
	// We do not want to update the member's details in the RFQ flow if they're logged in
	if (member) {
		return false;
	}

	return new WeddingsApi({ category, member, formData })
		.upsert()
		.catch((error) => {
			if (apiRequestErrors) {
				apiRequestErrors.push(
					formatApiRequestError(API_REQUEST_ERRORS.WEDDING_SDK_UPSERT, error),
				);
			}
			Logger.error({
				log_message: 'WeddingsApi Error - InlineRfq',
				errorToLog: error,
				newRelic: true,
			});
		});
};

const updateMember = (
	inquiryData: Rfq.InquiryData,
	member: Redux.Membership['member'],
	apiRequestErrors?: Rfq.ApiRequestErrors,
) => {
	// We do not want to update the member's details in the RFQ flow if they're logged in
	if (member) {
		return false;
	}

	return new MembershipApi(member, inquiryData)
		.update()
		.catch((error: string) => {
			if (apiRequestErrors) {
				apiRequestErrors.push(
					formatApiRequestError(API_REQUEST_ERRORS.MEMBERSHIP_UPDATE, error),
				);
			}
			Logger.error({
				log_message: 'MembershipApi Error - InlineRfq',
				errorToLog: new Error(error),
				newRelic: true,
			});
		});
};

type GenerateCookieData = (
	cookie: Rfq.Cookie,
	code: Category.CategoryCode,
) => {
	cookieData: Rfq.CookieData;
	flexibleWeddingDate: boolean | undefined;
};

const generateCookieData: GenerateCookieData = (cookie, code) => {
	const {
		emailAddress,
		firstName,
		guest_count,
		lastName,
		[code]: categoryDetails,
		phoneNumber,
		xo_date_picker,
	} = cookie;
	const cookieData: Rfq.CookieData = {
		emailAddress,
		firstName,
		guestCount: guest_count?.display,
		lastName,
		phoneNumber,
		textarea: categoryDetails?.body,
		weddingDate: xo_date_picker?.weddingDate,
	};

	return {
		cookieData,
		flexibleWeddingDate: categoryDetails?.flexibleWeddingDate,
	};
};

const resolveSourceContent = (
	isMobile: boolean,
	isTablet: boolean,
	code: string,
): InlineRfqSourceContent => {
	if (code === 'BEA') {
		return 'Message Vendor CTA';
	}
	return !isMobile && !isTablet
		? 'inline RFQ pricing top'
		: 'inline RFQ CTA pricing';
};

const addVendorsToFavorites = async (
	weddingId: string | undefined,
	member: Membership.Member,
	vendors: unknown[] = [],
	apiRequestErrors?: Rfq.ApiRequestErrors,
) => {
	const cookieStorage = new CookieStorage();
	const token = cookieStorage.getItem(XO_SESSION_TOKEN) || undefined;
	const weddingClient = new WeddingsApi({ member });

	try {
		const weddingIdParam =
			weddingId ||
			(
				await weddingClient.getWedding().catch((error: any) => {
					if (apiRequestErrors) {
						apiRequestErrors.push(
							formatApiRequestError(API_REQUEST_ERRORS.WEDDING_SDK_GET, error),
						);
					}
					Logger.warn({
						log_message: '[addVendorsToFavorites] Error fetching wedding',
					});
					throw error;
				})
			)?.id;

		if (token && weddingIdParam && vendors.length > 0) {
			return Promise.allSettled(
				vendors.map((vendor) => {
					return savedVendors
						.create({
							sessionToken: token,
							weddingId: weddingIdParam,
							data: formatSavedVendorData(vendor),
						})
						.catch((error: any) => {
							if (apiRequestErrors) {
								apiRequestErrors.push(
									formatApiRequestError(
										API_REQUEST_ERRORS.SAVED_VENDORS_CREATE,
										error,
									),
								);
							}
							Logger.warn({
								log_message: `[addVendorsToFavorites] error adding vendor: ${error}`,
							});
						});
				}),
			);
		}
		return Promise.resolve([]);
	} catch (error) {
		Logger.warn({
			log_message: `[addVendorsToFavorites] error adding vendors: ${error}`,
		});
		return Promise.resolve([]);
	}
};

export {
	buildInquiryData,
	changeUserPassword,
	determineFieldValues,
	fetchTemporaryPassword,
	generateCookieData,
	generateTemporaryPassword,
	getTokenForUser,
	logInUser,
	pickRandomValue,
	PW_REQUIREMENTS_LIST,
	resolveSourceContent,
	shuffleString,
	signUpUser,
	updateLocalStorage,
	upsertWeddingForUser,
	upsertWeddingDateForUser,
	updateMember,
	updateWeddings,
	addVendorsToFavorites,
	buildRfqDataFromMember,
};
